<template>
    <div class="table-toolbar">
        <div class="toolbar-left">
            <div v-if="features?.search" class="search-wrapper">
                <span class="search-icon">
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                        <path
                            d="M10 6.5C10 8.433 8.433 10 6.5 10C4.567 10 3 8.433 3 6.5C3 4.567 4.567 3 6.5 3C8.433 3 10 4.567 10 6.5ZM9.304 10.011C8.528 10.632 7.556 11 6.5 11C4.015 11 2 8.985 2 6.5C2 4.015 4.015 2 6.5 2C8.985 2 11 4.015 11 6.5C11 7.556 10.632 8.528 10.011 9.304L12.854 12.146C13.049 12.342 13.049 12.658 12.854 12.854C12.658 13.049 12.342 13.049 12.146 12.854L9.304 10.011Z"
                            fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" />
                    </svg>
                </span>
                <input 
                    v-model="localSearch" 
                    type="text" class="search-input"
                    @keyup.enter="applySearch"  
                    :placeholder="searchPlaceholder ?? 'Filter records…'" />
                <button v-if="localSearch" class="clear-btn" @click="clearSearch" aria-label="Limpiar búsqueda">
                    <svg width="10" height="10" viewBox="0 0 15 15" fill="none">
                        <path
                            d="M11.782 4.032a.575.575 0 1 0-.813-.814L7.5 6.687 4.032 3.218a.575.575 0 0 0-.814.814L6.687 7.5l-3.469 3.468a.575.575 0 0 0 .814.814L7.5 8.313l3.469 3.469a.575.575 0 0 0 .813-.814L8.313 7.5l3.469-3.468Z"
                            fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" />
                    </svg>
                </button>
            </div>
            <slot name="left-actions" />
        </div>

        <div class="toolbar-right">
            <slot name="right-actions" />

            <div v-if="totalRecords !== undefined" class="records-badge">
                <span class="records-dot"></span>
                {{ totalRecords.toLocaleString('en-US') }} records
            </div>

            <div v-if="totalRecords !== undefined && features?.exportExcel" class="toolbar-divider"></div>

            <button v-if="features?.exportExcel" class="export-btn" @click="$emit('export')" title="Exportar a Excel">
                <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
                    <path
                        d="M3.5 2C3.224 2 3 2.224 3 2.5v10c0 .276.224.5.5.5h8c.276 0 .5-.224.5-.5v-7H9.5C9.224 5 9 4.776 9 4.5V2H3.5ZM10 2.707L11.793 4.5H10V2.707ZM2 2.5C2 1.672 2.672 1 3.5 1h6a.5.5 0 0 1 .354.146l2.5 2.5A.5.5 0 0 1 12.5 4v8.5c0 .828-.672 1.5-1.5 1.5h-8C2.672 14 2 13.328 2 12.5v-10ZM5.5 7a.5.5 0 0 0 0 1h4a.5.5 0 0 0 0-1h-4Zm0 2a.5.5 0 0 0 0 1h4a.5.5 0 0 0 0-1h-4Zm0-4a.5.5 0 0 0 0 1H7a.5.5 0 0 0 0-1H5.5Z"
                        fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" />
                </svg>
                <span class="export-label">Exportar</span>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

interface Features {
    search?: boolean
    exportExcel?: boolean
}

const props = defineProps<{
    search?: string
    features?: Features
    searchPlaceholder?: string
    totalRecords?: number
}>()

const emit = defineEmits<{
    (e: 'update:search', value: string): void
    (e: 'export'): void
    (e: 'search', value: string): void
}>()

const localSearch = ref(props.search ?? '')

watch(() => props.search, (val) => {
    localSearch.value = val ?? ''
})

const applySearch = () => {
    const value = localSearch.value.trim()
    emit('update:search', value)
    emit('search', value)
}

const clearSearch = () => {
    localSearch.value = ''
    emit('update:search', '')
    emit('search', '')
}
</script>

<style scoped>
.table-toolbar {
    --tb-bg: linear-gradient(135deg, #f8faff 0%, #f3f4f6 100%);
    --tb-border: #e5e7eb;
    --tb-radius: 10px;
    --tb-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 4px 12px rgba(0, 0, 0, 0.04);
    --tb-gap: 0.5rem;

    --search-bg: #f9fafb;
    --search-border: #e5e7eb;
    --search-focus: #6366f1;
    --search-ring: rgba(99, 102, 241, 0.12);

    --text-muted: #9ca3af;
    --text-body: #374151;
    --text-dark: #111827;

    --green-bg: #f0fdf4;
    --green-border: #bbf7d0;
    --green-text: #15803d;
    --green-hover: #dcfce7;

    --pill-bg: #f3f4f6;
    --dot-color: #10b981;

    --font: 'DM Sans', system-ui, -apple-system, sans-serif;
}

.table-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--tb-gap);
    padding: 0.625rem 0.875rem;
    background: var(--tb-bg);
    border: 1px solid var(--tb-border);
    background-clip: padding-box;
    box-shadow: var(--tb-shadow);
    font-family: var(--font);
    margin-bottom: 0.75rem;
}

.toolbar-left,
.toolbar-right {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--tb-gap);
}

.search-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.search-icon {
    position: absolute;
    left: 0.65rem;
    color: var(--text-muted);
    pointer-events: none;
    display: flex;
    transition: color 0.15s;
}

.search-wrapper:focus-within .search-icon {
    color: var(--search-focus);
}

.search-input {
    width: 240px;
    height: 34px;
    padding: 0 2rem 0 2.1rem;
    font-size: 0.8125rem;
    font-family: var(--font);
    color: var(--text-dark);
    background: var(--search-bg);
    border: 1px solid var(--search-border);
    border-radius: 7px;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}

.search-input::placeholder {
    color: var(--text-muted);
}

.search-input:focus {
    background: #fff;
    border-color: var(--search-focus);
    box-shadow: 0 0 0 3px var(--search-ring);
}

.clear-btn {
    position: absolute;
    right: 0.45rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    padding: 0;
    background: #e5e7eb;
    border: none;
    border-radius: 50%;
    color: var(--text-body);
    cursor: pointer;
    transition: background 0.1s;
}

.clear-btn:hover {
    background: #d1d5db;
}

.toolbar-divider {
    width: 1px;
    height: 18px;
    background: var(--tb-border);
    flex-shrink: 0;
}

.records-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.22rem 0.625rem;
    font-size: 0.74rem;
    font-weight: 500;
    color: var(--text-body);
    background: var(--pill-bg);
    border-radius: 20px;
    white-space: nowrap;
    font-family: var(--font);
    letter-spacing: 0.01em;
}

.records-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--dot-color);
    flex-shrink: 0;
}

.export-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    height: 34px;
    padding: 0 0.75rem;
    font-size: 0.8125rem;
    font-weight: 500;
    font-family: var(--font);
    color: var(--green-text);
    background: var(--green-bg);
    border: 1px solid var(--green-border);
    border-radius: 7px;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
}

.export-btn:hover {
    background: var(--green-hover);
    border-color: #86efac;
    box-shadow: 0 1px 4px rgba(16, 163, 74, 0.12);
}

.export-btn:active {
    transform: translateY(1px);
}

.export-label {
    display: none;
}

@media (min-width: 480px) {
    .export-label {
        display: inline;
    }
}

@media (max-width: 479px) {
    .search-input {
        width: 180px;
    }
}
</style>